import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { authorizeClient, client } from '../../networking/client';
import {
  SalesOrderInfo,
  fetchSalesOrders,
  resetUpdateSalesOrderAppointmentStatus,
  selectSalesOrderAppointmentUpdateStatus,
} from '../../store/slices/salesOrders';
import {
  selectConsultationStatusUpdateStatus,
  resetUpdateStatus,
} from '../../store/slices/workOrderServiceConsultation';
import { useAppDispatch, useAppSelector } from '../../store/store';
import { createWorkOrderService } from '../../store/slices/workOrderServiceConsultation';

import {
  IAccountInfo,
  selectAccountInfo,
  selectBusinessPartnerId,
  selectDirtyState,
  selectUserLocation,
  setDirtyState,
} from '../../store/slices/auth';
import { formatTime, getInitialsFromName } from '../../utils/miscUtils';
import { useCheckMobileScreen } from '../../hooks';
import eyeson from 'eyeson';
import callIcon from '../../assets/icons/call-icon.svg';
import microphoneIcon from '../../assets/icons/microphoneOn.svg';
import microphoneIconMuted from '../../assets/icons/microphoneOff.svg';
import videoIcon from '../../assets/icons/cameraOn.svg';
import videoIconStopped from '../../assets/icons/cameraOff.svg';
import fullScreenOn from '../../assets/icons/full-screen-on-white.svg';
import fullScreenOff from '../../assets/icons/full-screen-off-white.svg';
import Spinner from '../../components/UI/Spinner/Spinner';
import CloseButton from '../../assets/icons/close-button.png';
import PrimaryButtonV2 from '../../components/UI/Buttons/PrimaryButtonV2';
import Modal from '../../components/UI/Modals/Modal';
import useTimer from '../../hooks/UseTimer';
import classes from './VideoConsultingRoom.module.scss';
import { useConsultationContext } from '../../context/ConsultationContext';
import EmptyScreen from './EmptyScreen';
import { useTranslation } from '../../hooks/useTranslations';

const VideoConsultingRoom: React.FC<any> = ({
  selectedSalesOrder,
}: {
  selectedSalesOrder: SalesOrderInfo;
}) => {
  const { translations } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const userLocation: string = useAppSelector<string>(selectUserLocation);
  const userInitials = getInitialsFromName(userLocation);

  const [pageInfo] = useState<any>({
    ...selectedSalesOrder,
    person: selectedSalesOrder?.customer_person,
    sales_order_item: selectedSalesOrder?.sales_order_item,
    work_order: {
      ...selectedSalesOrder?.work_order,
      work_order_id: selectedSalesOrder?.work_order?.work_order_id,
    },
  });
  const [isLoadingEyeson, setIsLoadingEyeson] = useState<boolean>(true);
  let video: any = useRef<HTMLVideoElement>();
  let videoLocal: any = useRef<HTMLVideoElement>();
  const [skip, setSkip] = useState<boolean>(true);
  const [hasAudio, setHasAudio] = useState<boolean>(true);
  const [hasVideo, setHasVideo] = useState<boolean>(true);
  const { timer, handleStart, handleReset, setTimer } = useTimer(0);
  const [hasCallEnded, setHasCallEnded] = useState<boolean>(false);
  const [isConnected, setIsConnected] = useState(false);
  const [fullScreen, setFullScreen] = useState<boolean>(false);
  const isMobile = useCheckMobileScreen();
  const [showNotification, setShowNotification] = useState<boolean>(false);
  const [timeLeft, setTimeLeft] = useState<number>(5);
  const [consultationEndTime, setConsultationEndTime] = useState<any>(undefined);
  const isDirtyState = useAppSelector(selectDirtyState);
  const { closeConsultation } = useConsultationContext();

  // const [infoPartExtended] = useState<boolean>(false);
  // const [showShareScreen, setShowShareScreen] = useState<boolean>(false);
  const salesOrderAppointmentUpdateStatus: string = useAppSelector<string>(
    selectSalesOrderAppointmentUpdateStatus
  );
  const consultationStatusUpdateStatus: string = useAppSelector<string>(
    selectConsultationStatusUpdateStatus
  );
  const accountInfo = useAppSelector<IAccountInfo>(selectAccountInfo);
  const businessPartnerId = useAppSelector<string>(selectBusinessPartnerId);

  useEffect(() => {
    dispatch(
      createWorkOrderService({
        serviceId: pageInfo?.service.service_id,
        salesOrderItemId: pageInfo?.sales_order_item?.sales_order_item_id,
        salesOrderId: pageInfo?.sales_order.sales_order_id,
        personId: pageInfo?.sales_order.business_person_id,
        businessPartnerId: businessPartnerId,
      })
    );

    dispatch(fetchSalesOrders());

    video = document.querySelector('#videostream');
    video.setAttribute('autoplay', '');
    video.setAttribute('playsinline', '');

    videoLocal = document.querySelector('#videolocal');
    videoLocal.setAttribute('autoplay', '');
    videoLocal.setAttribute('muted', '');
    videoLocal.setAttribute('playsinline', '');
    createRoomApiCall();
    const startedAt =
      Math.floor((new Date().getTime() - new Date(eyeson?.room?.started_at).getTime()) / 1000) || 0;
    setTimer(startedAt);
    handleStart();
    const consultationStarted = new Date(eyeson?.room?.started_at);
    setConsultationEndTime(consultationStarted.setMinutes(consultationStarted.getMinutes() + 15));
    return function cleanup() {
      eyeson.destroy();
      if (videoLocal && videoLocal.srcObject) {
        videoLocal.srcObject.getTracks().forEach((track: any) => track.stop());
      }
      handleReset();
    };
  }, []);

  useEffect(() => {
    if (!skip) {
      eyeson.send({
        type: 'change_stream',
        audio: hasAudio,
        video: hasVideo,
      });
    }
  }, [hasAudio, hasVideo, skip]);

  useEffect(() => {
    if (
      salesOrderAppointmentUpdateStatus === 'fulfilled' ||
      salesOrderAppointmentUpdateStatus === 'rejected'
    ) {
      dispatch(resetUpdateSalesOrderAppointmentStatus());
      if (salesOrderAppointmentUpdateStatus === 'fulfilled') {
        navigate('/web/schedule');
      }
    }
  }, [salesOrderAppointmentUpdateStatus]);

  useEffect(() => {
    if (consultationStatusUpdateStatus === 'fulfilled') {
      dispatch(resetUpdateStatus());
      // navigate('/web/schedule');
    }
    if (consultationStatusUpdateStatus === 'rejected') {
      dispatch(resetUpdateStatus());
    }
  }, [consultationStatusUpdateStatus, dispatch]);

  // Function to calculate time remaining in minutes
  const calculateTimeLeft = () => {
    const now: any = new Date();
    const endTime: any = new Date(consultationEndTime);
    const difference = (endTime - now) / 1000 / 60; // difference in minutes
    return Math.ceil(difference); // Round up to the nearest minute
  };

  useEffect(() => {
    if (consultationEndTime) {
      const interval = setInterval(() => {
        const remainingTime = calculateTimeLeft();
        if (remainingTime <= 5 && remainingTime > 0) {
          setTimeLeft(remainingTime);
          setShowNotification(true);
        } else if (remainingTime <= 0) {
          setShowNotification(false);
          clearInterval(interval);
        }
      }, 1000 * 60);

      return () => clearInterval(interval);
    }
  }, [consultationEndTime]);

  const createRoomApiCall = async () => {
    if (pageInfo?.work_order?.work_order_status_id === 2) {
      setIsLoadingEyeson(false);
      setHasCallEnded(true);
      return;
    }
    // const selectedSalesOrder: any = location.state;
    const taId = selectedSalesOrder.sales_order?.ta_id ?? '';
    const productOrderId = selectedSalesOrder.sales_order?.sales_order_external_id ?? '';
    const productOrderItemId =
      selectedSalesOrder.sales_order_item?.external_sales_order_item_id ?? '';
    eyeson.onEvent((event: any) => handleEvent(event));
    try {
      let stream: MediaStream;
      stream = await navigator.mediaDevices.getUserMedia({ video: true, audio: true });
      video.srcObject = stream;
      video.play();
      videoLocal.muted = true;
      videoLocal.srcObject = stream;
      videoLocal.setAttribute('style', 'display:none');
      videoLocal.play();
      const getSalesOrderAppointmentResponse = await client.get(
        `/healthpoint/sales-order-appointment/${pageInfo?.sales_order_appointment.sales_order_appointment_id}`
      );
      if (getSalesOrderAppointmentResponse.data.salesOrderAppointment.access_key) {
        eyeson.connect(getSalesOrderAppointmentResponse.data.salesOrderAppointment.access_key);
      } else {
        const createRoomResponse: any = await authorizeClient.post(
          `/core/video_consultation/rooms_merchant?merchant_account_number=${accountInfo.account_number}&ta_id=${taId}&product_order_id=${productOrderId}&product_order_item_id=${productOrderItemId}`
        );
        await client.put(
          `/healthpoint/sales-order-appointment/eyeson/${pageInfo?.sales_order_appointment.sales_order_appointment_id}`,
          {
            accessKey: createRoomResponse.data.access_key,
            guestToken: createRoomResponse.data.room?.guest_token,
          }
        );
        eyeson.connect(createRoomResponse.data.access_key);
      }

      // await eyeson.connect('dJ1Jkw4EAxUduWkUEdUkoiOS');
      //https://app.eyeson.team/?guest=CqTRzEgo15Wo9Xv2tPgBO1WC
    } catch (_) {
      setIsLoadingEyeson(false);
    }
  };

  const handleEvent = (event: any) => {
    if (!event) {
      return;
    }
    let videoElement: any = document.getElementById('videostream');
    // let videoElementLocal: any = document.getElementById('videolocal');

    switch (event.type) {
      case 'connection': {
        if (event.connectionStatus === 'ready' || event.connectionStatus === 'connected') {
          eyeson.join({ audio: true, video: true, stream: videoElement.srcObject });
        } else if (event.connectionStatus === 'access_denied') {
          setIsLoadingEyeson(false);
          toast.error(translations('placeholder.access.key'), {
            position: toast.POSITION.BOTTOM_RIGHT,
          });
          quitSession();
          let videoLocal2: any = document.querySelector('#videolocal');
          if (videoLocal2 && videoLocal2.srcObject) {
            videoLocal2.srcObject.getTracks().forEach((track: any) => track.stop());
          }
        }
        break;
      }
      case 'accept': {
        videoElement.srcObject = event.remoteStream;
        videoElement.play();
        setIsLoadingEyeson(false);
        const consultationStarted = new Date(eyeson?.room?.started_at);
        setConsultationEndTime(
          consultationStarted.setMinutes(consultationStarted.getMinutes() + 15)
        );

        break;
      }
      case 'add_user': {
        if (event.user.guest) {
          videoLocal.setAttribute('style', 'display:inline');
          videoElement.setAttribute('style', 'display:inline');
          video.setAttribute('style', 'display:inline');
          setIsConnected(true);
        }
        break;
      }
      case 'remove_user': {
        videoLocal.setAttribute('style', 'display:none');
        break;
      }
      default:
        break;
    }
  };

  const quitSession = () => {
    eyeson.destroy();
    setHasCallEnded(true);
  };

  const editStream = async (event: any) => {
    try {
      await navigator.mediaDevices.getUserMedia({ audio: true, video: true });
      setSkip(false);
      event.target.alt === 'microphoneIcon'
        ? setHasAudio((prevHasAudio) => !prevHasAudio)
        : setHasVideo((prevHasVideo) => !prevHasVideo);
    } catch (error: any) {
      if (error.name === 'NotAllowedError') {
        alert(
          'Permissions were denied. Please enable camera and microphone access in your browser settings.'
        );
      } else if (error.name === 'NotFoundError') {
        alert('No camera or microphone found. Please connect a device.');
      } else {
        alert('An unknown error occurred.');
      }
    }
  };

  // const startShareScreen = () => {
  //   setShowShareScreen(true);
  //   eyeson.send({ type: 'start_stream', audio: hasAudio, video: false, screen: true });
  // };

  // const endShareScreen = () => {
  //   setShowShareScreen(false);
  //   eyeson.send({ type: 'change_stream', audio: hasAudio, video: hasVideo, screen: false });
  // };

  const handleFullScreen = () => {
    setFullScreen(!fullScreen);
  };

  const handleModal = (type: 'yes' | 'no') => {
    if (type === 'yes') {
      dispatch(
        setDirtyState({ isDirty: false, isModalOpen: false, component: 'videoConsultationRoom' })
      );
      closeConsultation(true);
    } else {
      dispatch(
        setDirtyState({ isDirty: true, isModalOpen: false, component: 'videoConsultationRoom' })
      );
    }
  };

  return (
    <div style={{ height: '100%' }}>
      <div
        className={
          !fullScreen ? `${classes.container}` : `${classes.container} ${classes.fullScreen}`
        }
      >
        {showNotification && (
          <div className={`${classes.messageContainer} ${isMobile ? classes.mobile : ''}`}>
            <p className={classes.textMessage}>{`${timeLeft} minutes left.`}</p>
            <div className={classes.buttonStyle} onClick={() => setShowNotification(false)}>
              <img src={CloseButton} style={{ width: '12px' }} alt="Close Button" />
            </div>
          </div>
        )}

        {!hasCallEnded ? (
          <div className={`${classes.videoContainer}`} style={{ flex: 1 }}>
            <div className="video" style={{ height: '100%' }}>
              <div className={classes.videoStreamContainer}>
                <video
                  id="videolocal"
                  className={hasVideo ? classes.localVideo : classes.localVideoHide}
                  // style={}
                  playsInline
                  autoPlay
                  muted
                ></video>

                {isConnected && !hasVideo ? (
                  <div className={classes.localVideoHide}>
                    {userInitials ? <div className={classes.cameraOff}>{userInitials}</div> : 'YOU'}
                  </div>
                ) : null}
              </div>

              <div className={classes.videoStreamContainer} style={{ height: '100%' }}>
                <video
                  id="videostream"
                  className={hasVideo ? classes.videoStream : classes.videoStreamHide}
                ></video>
                {!isConnected && !hasVideo ? (
                  <div className={classes.cameraOffOverlay}>
                    {userInitials ? <div className={classes.cameraOff}>{userInitials}</div> : 'YOU'}
                  </div>
                ) : null}
              </div>
            </div>
            <div className={classes.videoControlsContainer}>
              <div className={classes.timeStamp}>{formatTime(timer)}</div>
              {/* <div className={classes.videoControlsMid}></div> */}
              <div className={classes.videoControlsMid}>
                <img
                  src={hasAudio ? microphoneIcon : microphoneIconMuted}
                  className={classes.callIcon}
                  alt="microphoneIcon"
                  onClick={(e) => editStream(e)}
                />
                <img
                  src={callIcon}
                  className={classes.callIcon}
                  alt="callIcon"
                  onClick={quitSession}
                />
                <img
                  src={hasVideo ? videoIcon : videoIconStopped}
                  className={classes.callIcon}
                  alt="videoIcon"
                  onClick={(e) => editStream(e)}
                />
              </div>
              <img
                src={fullScreen ? fullScreenOff : fullScreenOn}
                className={classes.fullScreenToggle}
                alt={'full-screen-on'}
                onClick={handleFullScreen}
              />
              {/* <div className={classes.videoControlsRight}>
              {!showShareScreen ? (
                <img
                  src={shareIcon}
                  className={classes.callIcon}
                  alt="callOptions"
                  onClick={startShareScreen}
                />
              ) : (
                <img
                  src={stopSharingIcon}
                  className={classes.callIcon}
                  alt="callOptions"
                  onClick={endShareScreen}
                />
              )}

              <img
                src={callOptionsIcon}
                className={classes.callIcon}
                alt="callOptions"
                onClick={() => {
                  setShowPatientDetails((prevShowPatientDetails) => !prevShowPatientDetails);
                }}
              />
            </div> */}
            </div>
            {isLoadingEyeson && (
              <div className={classes.loadingVideo}>
                <Spinner />
              </div>
            )}
          </div>
        ) : (
          <EmptyScreen
            fullName={`${pageInfo?.person?.person_firstname} ${pageInfo?.person?.person_lastname}`}
            style={{ width: '100%' }}
          />
        )}
      </div>
      <Modal
        isOpen={isDirtyState.isModalOpen}
        onClose={() =>
          dispatch(
            setDirtyState({ isDirty: true, isModalOpen: false, component: 'videoConsultationRoom' })
          )
        }
        containerPadding={'20px 10px'}
      >
        <div style={{ textAlign: 'center' }}>
          <div style={{ padding: '20px 40px', lineHeight: '22px' }}>
            {translations('customer_did_not_join.modal')}
          </div>
        </div>
        <div style={{ display: 'flex', justifyContent: 'center', marginTop: 40 }}>
          <PrimaryButtonV2
            text={translations('yes')}
            containerStyle={{ marginRight: 20 }}
            buttonStyle={{ height: 40, width: 130 }}
            onClick={() => handleModal('yes')}
            disabled={false}
          />
          <PrimaryButtonV2
            text={translations('no')}
            invertedColors
            buttonStyle={{ height: 40, width: 130 }}
            onClick={() => handleModal('no')}
            disabled={false}
          />
        </div>
      </Modal>
    </div>
  );
};

export default VideoConsultingRoom;
